import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { Layout } from '../../components/Layout';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../../components/ui/dialog';
import { getInitials } from '../../lib/utils';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Textarea } from '../../components/ui/textarea';
import { Avatar, AvatarFallback } from '../../components/ui/avatar';
import { Popover, PopoverContent, PopoverTrigger } from '../../components/ui/popover';
import { Calendar } from '../../components/ui/calendar';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { toast } from 'sonner';
import { format, parseISO } from 'date-fns';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer
} from 'recharts';
import { 
  Building2,
  Mail,
  Phone,
  MapPin,
  Clock,
  FolderKanban,
  ArrowLeft,
  Edit,
  Download,
  ChevronDown,
  CalendarIcon,
  MessageSquare,
  Activity,
  Plus,
  Send,
  FileText,
  ExternalLink,
  User,
  Globe,
  Receipt
} from 'lucide-react';
import { useTranslation } from 'react-i18next';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Format phone number for display (doesn't change stored value)
const formatPhoneDisplay = (phone) => {
  if (!phone) return phone;
  // Remove all non-digit characters except +
  const cleaned = phone.replace(/[^\d+]/g, '');
  // Indian numbers: +91 XXXXX XXXXX
  if (cleaned.startsWith('+91') && cleaned.length === 13) {
    return `+91 ${cleaned.slice(3, 8)} ${cleaned.slice(8)}`;
  }
  // US numbers: +1 XXX XXX XXXX
  if (cleaned.startsWith('+1') && cleaned.length === 12) {
    return `+1 ${cleaned.slice(2, 5)} ${cleaned.slice(5, 8)} ${cleaned.slice(8)}`;
  }
  // Other international: add spaces after country code
  if (cleaned.startsWith('+') && cleaned.length > 10) {
    const countryCodeEnd = cleaned.length > 12 ? 3 : 2;
    return `${cleaned.slice(0, countryCodeEnd)} ${cleaned.slice(countryCodeEnd, countryCodeEnd + 5)} ${cleaned.slice(countryCodeEnd + 5)}`;
  }
  // Local 10-digit: XXXXX XXXXX
  if (cleaned.length === 10) {
    return `${cleaned.slice(0, 5)} ${cleaned.slice(5)}`;
  }
  return phone;
};


export default function ClientDetail() {
  const { user, token } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { clientId } = useParams();
  
  // State
  const [client, setClient] = useState(null);
  const [hoursData, setHoursData] = useState(null);
  const [loading, setLoading] = useState(true);
  
  // Time period
  const [period, setPeriod] = useState('month');
  const [customStart, setCustomStart] = useState(null);
  const [customEnd, setCustomEnd] = useState(null);
  
  // Notes / Activity Log
  const [newNote, setNewNote] = useState('');
  const [activityLog, setActivityLog] = useState([]);
  
  // Tab
  const [activeTab, setActiveTab] = useState('overview');

  // Edit modal
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editForm, setEditForm] = useState({ name: '', contact_name: '', contact_email: '', contact_phone: '', address: '', notes: '', status: 'active', gst_id: '', gst_treatment: '', currency: 'INR' });

  const openEditModal = () => {
    if (client) {
      setEditForm({
        name: client.name || '',
        contact_name: client.contact_name || '',
        contact_email: client.contact_email || '',
        contact_phone: client.contact_phone || '',
        address: client.address || '',
        notes: client.notes || '',
        status: client.status || 'active',
        gst_id: client.gst_id || '',
        gst_treatment: client.gst_treatment || '',
        currency: client.currency || 'INR',
      });
      setEditModalOpen(true);
    }
  };

  const handleEditSave = async () => {
    if (!editForm.name.trim()) { toast.error('Client name is required'); return; }
    try {
      const res = await fetch(`${API_URL}/api/admin/clients/${clientId}`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(editForm)
      });
      if (!res.ok) { const err = await res.json(); throw new Error(err.detail || 'Failed to update'); }
      toast.success('Client updated successfully');
      setEditModalOpen(false);
      fetchClient();
    } catch (e) { toast.error(e.message); }
  };

  // Access check
  useEffect(() => {
    if (user && !['admin', 'manager'].includes(user.role)) {
      navigate('/dashboard');
      toast.error('You do not have access to this page');
    }
  }, [user, navigate]);

  // Fetch client details
  const fetchClient = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/admin/clients/${clientId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (!res.ok) {
        if (res.status === 404) {
          toast.error('Client not found');
          navigate('/admin/clients');
          return;
        }
        throw new Error('Failed to fetch client');
      }
      
      const data = await res.json();
      setClient(data);
      setActivityLog(data.activity_log || []);
    } catch (error) {
      console.error('Failed to fetch client:', error);
      toast.error('Failed to load client details');
    }
  }, [token, clientId, navigate]);

  // Fetch hours data
  const fetchHours = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      params.append('period', period);
      if (period === 'custom' && customStart) params.append('start_date', format(customStart, 'yyyy-MM-dd'));
      if (period === 'custom' && customEnd) params.append('end_date', format(customEnd, 'yyyy-MM-dd'));

      const res = await fetch(`${API_URL}/api/admin/clients/${clientId}/hours?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const data = await res.json();
      setHoursData(data);
    } catch (error) {
      console.error('Failed to fetch hours:', error);
    }
  }, [token, clientId, period, customStart, customEnd]);

  useEffect(() => {
    if (token && clientId) {
      setLoading(true);
      fetchClient().finally(() => setLoading(false));
    }
  }, [token, clientId, fetchClient]);

  useEffect(() => {
    if (token && clientId) {
      fetchHours();
    }
  }, [token, clientId, fetchHours]);

  // Add note
  const handleAddNote = async () => {
    if (!newNote.trim()) {
      toast.error('Please enter a note');
      return;
    }

    try {
      const res = await fetch(`${API_URL}/api/admin/clients/${clientId}/notes`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ content: newNote.trim() })
      });

      if (!res.ok) throw new Error('Failed to add note');

      const note = await res.json();
      setActivityLog([note, ...activityLog]);
      setNewNote('');
      toast.success('Note added');
    } catch (error) {
      toast.error(error.message);
    }
  };

  // Export
  const handleExport = async (format) => {
    try {
      const params = new URLSearchParams();
      params.append('format', format);
      if (period === 'custom' && customStart) params.append('start_date', format(customStart, 'yyyy-MM-dd'));
      if (period === 'custom' && customEnd) params.append('end_date', format(customEnd, 'yyyy-MM-dd'));

      const res = await fetch(`${API_URL}/api/admin/clients/export/report?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${client?.name || 'client'}_report_${new Date().toISOString().slice(0, 10)}.${format}`;
      a.click();
      toast.success(`${format.toUpperCase()} downloaded`);
    } catch (error) {
      toast.error('Failed to export');
    }
  };

  // Helpers
  const getStatusBadge = (status) => {
    switch (status) {
      case 'active': return 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/20 dark:text-emerald-300';
      case 'inactive': return 'bg-slate-100 dark:bg-slate-700 text-slate-700 dark:bg-slate-50 dark:bg-slate-7000/20 dark:text-slate-300';
      case 'archived': return 'bg-amber-100 text-amber-700 dark:bg-amber-900/20 dark:text-amber-300';
      case 'on_hold': return 'bg-amber-100 text-amber-700 dark:bg-amber-900/20 dark:text-amber-300';
      default: return 'bg-slate-100 dark:bg-slate-700 text-slate-700';
    }
  };

  const formatDate = (dateStr) => {
    try {
      return format(parseISO(dateStr), 'MMM d, yyyy');
    } catch {
      return dateStr;
    }
  };

  const formatDateTime = (dateStr) => {
    try {
      return format(parseISO(dateStr), 'MMM d, yyyy h:mm a');
    } catch {
      return dateStr;
    }
  };

  const getNoteIcon = (type) => {
    switch (type) {
      case 'note': return <MessageSquare className="w-4 h-4 text-blue-500" />;
      case 'project_created': return <FolderKanban className="w-4 h-4 text-emerald-500" />;
      case 'status_change': return <Activity className="w-4 h-4 text-purple-500" />;
      default: return <FileText className="w-4 h-4 text-slate-400" />;
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      </Layout>
    );
  }

  if (!client) return null;

  return (
    <Layout>
      <div className="space-y-6" data-testid="client-detail-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" onClick={() => navigate('/admin/clients')} data-testid="back-btn">
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <Avatar className="h-14 w-14">
              <AvatarFallback className="bg-gradient-to-br from-blue-500 to-purple-600 text-white text-xl">
                {getInitials(client.name)}
              </AvatarFallback>
            </Avatar>
            <div>
              <div className="flex items-center gap-2">
                <h1 className="text-2xl font-bold text-slate-900 dark:text-white">{client.name}</h1>
                <Badge className={getStatusBadge(client.status)}>{client.status}</Badge>
              </div>
              <p className="text-slate-500 dark:text-slate-400">
                Created {formatDate(client.created_at)} • {client.projects?.length || 0} projects
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Popover>
              <PopoverTrigger asChild>
                <Button variant="outline" data-testid="export-btn">
                  <Download className="w-4 h-4 mr-2" />
                  Export
                  <ChevronDown className="w-4 h-4 ml-2" />
                </Button>
              </PopoverTrigger>
              <PopoverContent align="end" className="w-40">
                <Button variant="ghost" className="w-full justify-start" onClick={() => handleExport('csv')}>
                  CSV
                </Button>
                <Button variant="ghost" className="w-full justify-start" onClick={() => handleExport('pdf')}>
                  PDF
                </Button>
              </PopoverContent>
            </Popover>
            {user?.role === 'admin' && (
              <Button variant="outline" onClick={openEditModal} data-testid="edit-client-btn">
                <Edit className="w-4 h-4 mr-2" />
                Edit Client
              </Button>
            )}
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <Card>
            <CardContent className="p-4">
              <p className="text-2xl font-bold text-slate-900 dark:text-white" data-testid="total-hours">
                {client.total_hours}h
              </p>
              <p className="text-sm text-slate-500">Total Hours (All Time)</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <p className="text-2xl font-bold text-slate-900 dark:text-white">
                {hoursData?.total_hours || 0}h
              </p>
              <p className="text-sm text-slate-500">Hours This Period</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <p className="text-2xl font-bold text-slate-900 dark:text-white">
                {client.projects?.filter(p => p.status === 'active').length || 0}
              </p>
              <p className="text-sm text-slate-500">Active Projects</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <p className="text-2xl font-bold text-slate-900 dark:text-white">
                {client.projects?.length || 0}
              </p>
              <p className="text-sm text-slate-500">Total Projects</p>
            </CardContent>
          </Card>
        </div>

        {/* Main Content Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
          <TabsList>
            <TabsTrigger value="overview" data-testid="tab-overview">Overview</TabsTrigger>
            <TabsTrigger value="projects" data-testid="tab-projects">Projects</TabsTrigger>
            <TabsTrigger value="hours" data-testid="tab-hours">Hours</TabsTrigger>
            <TabsTrigger value="notes" data-testid="tab-notes">Notes & Activity</TabsTrigger>
          </TabsList>

          {/* OVERVIEW TAB */}
          <TabsContent value="overview" className="space-y-6">
            {/* Client Info - Full Width */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Building2 className="w-5 h-5" />
                  Client Information
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid md:grid-cols-2 gap-6">
                  {/* Left column */}
                  <div className="space-y-4">
                    {client.contact_name && (
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg bg-blue-100 dark:bg-blue-900/20 flex items-center justify-center">
                          <User className="w-5 h-5 text-blue-600" />
                        </div>
                        <div>
                          <p className="text-sm text-slate-500">Contact Person</p>
                          <p className="font-medium">{client.contact_name}</p>
                        </div>
                      </div>
                    )}
                    {client.contact_email && (
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg bg-emerald-100 dark:bg-emerald-900/20 flex items-center justify-center">
                          <Mail className="w-5 h-5 text-emerald-600" />
                        </div>
                        <div>
                          <p className="text-sm text-slate-500">Email</p>
                          <a href={`mailto:${client.contact_email}`} className="font-medium text-blue-600 hover:underline">
                            {client.contact_email}
                          </a>
                        </div>
                      </div>
                    )}
                    {client.contact_phone && (
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg bg-purple-100 dark:bg-purple-900/20 flex items-center justify-center">
                          <Phone className="w-5 h-5 text-purple-600" />
                        </div>
                        <div>
                          <p className="text-sm text-slate-500">Phone</p>
                          <a href={`tel:${client.contact_phone}`} className="font-medium">
                            {formatPhoneDisplay(client.contact_phone)}
                          </a>
                        </div>
                      </div>
                    )}
                    {client.address && (
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg bg-amber-100 dark:bg-amber-900/20 flex items-center justify-center">
                          <MapPin className="w-5 h-5 text-amber-600" />
                        </div>
                        <div>
                          <p className="text-sm text-slate-500">Address</p>
                          <p className="font-medium">{client.address}</p>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Right column - Billing Info */}
                  <div className="space-y-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-cyan-100 dark:bg-cyan-500/20 flex items-center justify-center">
                        <Globe className="w-5 h-5 text-cyan-600" />
                      </div>
                      <div>
                        <p className="text-sm text-slate-500">Currency</p>
                        <p className="font-medium">{client.currency || 'INR'}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-orange-100 dark:bg-orange-900/20 flex items-center justify-center">
                        <Receipt className="w-5 h-5 text-orange-600" />
                      </div>
                      <div>
                        <p className="text-sm text-slate-500">GST Treatment</p>
                        <p className="font-medium">
                          {client.gst_treatment === 'registered' ? 'Registered (Has GSTIN)' :
                           client.gst_treatment === 'overseas' ? 'Overseas (Export)' :
                           client.gst_treatment === 'consumer' ? 'Consumer (Unregistered)' :
                           'Not Applicable'}
                        </p>
                      </div>
                    </div>
                    {client.gst_id && (
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg bg-rose-100 dark:bg-rose-500/20 flex items-center justify-center">
                          <FileText className="w-5 h-5 text-rose-600" />
                        </div>
                        <div>
                          <p className="text-sm text-slate-500">GST ID</p>
                          <p className="font-medium font-mono">{client.gst_id}</p>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {client.notes && (
                  <div className="pt-6 mt-6 border-t">
                    <p className="text-sm text-slate-500 mb-1">Notes</p>
                    <p className="text-slate-700 dark:text-slate-300">{client.notes}</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* PROJECTS TAB */}
          <TabsContent value="projects" className="space-y-4">
            <Card>
              <CardContent className="p-0">
                <div className="overflow-x-auto">
                  <table className="w-full" data-testid="projects-table">
                    <thead className="bg-slate-50 dark:bg-slate-800/50 border-b">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase">Project</th>
                        <th className="px-4 py-3 text-right text-xs font-semibold text-slate-500 uppercase">Hours</th>
                        <th className="px-4 py-3 text-center text-xs font-semibold text-slate-500 uppercase">Status</th>
                        <th className="px-4 py-3 text-right text-xs font-semibold text-slate-500 uppercase"></th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {client.projects?.map(project => (
                        <tr key={project.id} className="hover:bg-slate-50 dark:hover:bg-slate-800/50">
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-3">
                              <div className="w-3 h-3 rounded-full" style={{ backgroundColor: project.color }} />
                              <span className="font-medium">{project.name}</span>
                            </div>
                          </td>
                          <td className="px-4 py-3 text-right font-medium">{project.total_hours}h</td>
                          <td className="px-4 py-3 text-center">
                            <Badge className={getStatusBadge(project.status)}>{project.status}</Badge>
                          </td>
                          <td className="px-4 py-3 text-right">
                            <Link to={`/admin/projects/${project.id}`}>
                              <Button variant="ghost" size="sm">
                                <ExternalLink className="w-4 h-4" />
                              </Button>
                            </Link>
                          </td>
                        </tr>
                      ))}
                      {(!client.projects || client.projects.length === 0) && (
                        <tr>
                          <td colSpan={4} className="px-4 py-12 text-center text-slate-500">
                            No projects associated with this client
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* HOURS TAB */}
          <TabsContent value="hours" className="space-y-4">
            {/* Period Filter */}
            <Card>
              <CardContent className="p-4">
                <div className="flex flex-wrap items-center gap-4">
                  <Label className="font-medium">Period:</Label>
                  <Select value={period} onValueChange={setPeriod}>
                    <SelectTrigger className="w-[150px]" data-testid="period-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="week">This Week</SelectItem>
                      <SelectItem value="month">This Month</SelectItem>
                      <SelectItem value="custom">Custom Range</SelectItem>
                    </SelectContent>
                  </Select>
                  
                  {period === 'custom' && (
                    <div className="flex items-center gap-2">
                      <Popover>
                        <PopoverTrigger asChild>
                          <Button variant="outline" size="sm">
                            <CalendarIcon className="w-4 h-4 mr-2" />
                            {customStart ? format(customStart, 'MMM d') : 'Start'}
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-auto p-0">
                          <Calendar mode="single" selected={customStart} onSelect={setCustomStart} />
                        </PopoverContent>
                      </Popover>
                      <span className="text-slate-400">to</span>
                      <Popover>
                        <PopoverTrigger asChild>
                          <Button variant="outline" size="sm">
                            <CalendarIcon className="w-4 h-4 mr-2" />
                            {customEnd ? format(customEnd, 'MMM d') : 'End'}
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-auto p-0">
                          <Calendar mode="single" selected={customEnd} onSelect={setCustomEnd} />
                        </PopoverContent>
                      </Popover>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Hours Chart */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Daily Hours</CardTitle>
                <CardDescription>
                  Total: {hoursData?.total_hours || 0}h for selected period
                </CardDescription>
              </CardHeader>
              <CardContent>
                {hoursData?.daily_breakdown && hoursData.daily_breakdown.length > 0 ? (
                  <div className="h-[300px]">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={hoursData.daily_breakdown}>
                        <XAxis dataKey="date" tickFormatter={(d) => format(parseISO(d), 'MMM d')} fontSize={12} />
                        <YAxis fontSize={12} />
                        <Tooltip 
                          formatter={(value) => [`${value}h`, 'Hours']}
                          labelFormatter={(d) => format(parseISO(d), 'EEEE, MMM d')}
                        />
                        <Bar dataKey="hours" fill="#3B82F6" radius={[4, 4, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                ) : (
                  <div className="h-[300px] flex items-center justify-center text-slate-500">
                    No hours tracked for this period
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Project Breakdown */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Hours by Project</CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-slate-50 dark:bg-slate-800/50 border-b">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase">Project</th>
                        <th className="px-4 py-3 text-right text-xs font-semibold text-slate-500 uppercase">Hours</th>
                        <th className="px-4 py-3 text-right text-xs font-semibold text-slate-500 uppercase">%</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {hoursData?.by_project?.map((project, idx) => (
                        <tr key={idx} className="hover:bg-slate-50 dark:hover:bg-slate-800/50">
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-3">
                              <div className="w-3 h-3 rounded-full" style={{ backgroundColor: project.color }} />
                              <span className="font-medium">{project.name}</span>
                            </div>
                          </td>
                          <td className="px-4 py-3 text-right font-medium">{project.hours}h</td>
                          <td className="px-4 py-3 text-right text-slate-500">
                            {hoursData.total_hours > 0 ? Math.round((project.hours / hoursData.total_hours) * 100) : 0}%
                          </td>
                        </tr>
                      ))}
                      {(!hoursData?.by_project || hoursData.by_project.length === 0) && (
                        <tr>
                          <td colSpan={3} className="px-4 py-8 text-center text-slate-500">
                            No hours tracked for this period
                          </td>
                        </tr>
                      )}
                    </tbody>
                    {hoursData?.total_hours > 0 && (
                      <tfoot className="bg-slate-50 dark:bg-slate-800/50">
                        <tr>
                          <td className="px-4 py-3 font-semibold">Total</td>
                          <td className="px-4 py-3 text-right font-semibold">{hoursData.total_hours}h</td>
                          <td className="px-4 py-3 text-right text-slate-500">100%</td>
                        </tr>
                      </tfoot>
                    )}
                  </table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* NOTES TAB */}
          <TabsContent value="notes" className="space-y-4">
            {/* Add Note */}
            <Card>
              <CardContent className="p-4">
                <div className="flex gap-3">
                  <Textarea
                    value={newNote}
                    onChange={(e) => setNewNote(e.target.value)}
                    placeholder="Add a note about this client..."
                    rows={2}
                    className="flex-1"
                    data-testid="new-note-input"
                  />
                  <Button onClick={handleAddNote} disabled={!newNote.trim()} data-testid="add-note-btn">
                    <Send className="w-4 h-4 mr-2" />
                    Add Note
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Notes List */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Activity className="w-5 h-5" />
                  Activity Log
                </CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                <div className="divide-y">
                  {activityLog.map(note => (
                    <div key={note.id} className="p-4 hover:bg-slate-50 dark:hover:bg-slate-800/50">
                      <div className="flex items-start gap-3">
                        {getNoteIcon(note.note_type)}
                        <div className="flex-1">
                          <p className="text-slate-700 dark:text-slate-300">{note.content}</p>
                          <p className="text-xs text-slate-400 mt-1">
                            {note.user_name} • {formatDateTime(note.created_at)}
                          </p>
                        </div>
                      </div>
                    </div>
                  ))}
                  {activityLog.length === 0 && (
                    <div className="p-12 text-center text-slate-500">
                      <MessageSquare className="w-12 h-12 mx-auto text-slate-300 mb-3" />
                      <p>No notes or activity yet</p>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Edit Client Modal */}
        <Dialog open={editModalOpen} onOpenChange={setEditModalOpen}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>Edit Client</DialogTitle>
              <DialogDescription>Update client information</DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-2">
              <div>
                <Label>Client Name *</Label>
                <Input value={editForm.name} onChange={e => setEditForm(f => ({...f, name: e.target.value}))} data-testid="edit-client-name" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Contact Person</Label>
                  <Input value={editForm.contact_name} onChange={e => setEditForm(f => ({...f, contact_name: e.target.value}))} />
                </div>
                <div>
                  <Label>Contact Email</Label>
                  <Input value={editForm.contact_email} onChange={e => setEditForm(f => ({...f, contact_email: e.target.value}))} />
                </div>
              </div>
              <div>
                <Label>Contact Phone</Label>
                <Input value={editForm.contact_phone} onChange={e => setEditForm(f => ({...f, contact_phone: e.target.value}))} />
              </div>
              <div>
                <Label>Address</Label>
                <Textarea value={editForm.address} onChange={e => setEditForm(f => ({...f, address: e.target.value}))} rows={2} />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Currency <span className="text-xs text-slate-400 font-normal ml-1">(locked after creation)</span></Label>
                  <Select value={editForm.currency} onValueChange={v => setEditForm(f => ({...f, currency: v}))} disabled>
                    <SelectTrigger data-testid="edit-currency-select" className="opacity-60 cursor-not-allowed"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="INR">INR - Indian Rupee</SelectItem>
                      <SelectItem value="USD">USD - US Dollar</SelectItem>
                      <SelectItem value="EUR">EUR - Euro</SelectItem>
                      <SelectItem value="GBP">GBP - British Pound</SelectItem>
                      <SelectItem value="AED">AED - UAE Dirham</SelectItem>
                      <SelectItem value="SGD">SGD - Singapore Dollar</SelectItem>
                      <SelectItem value="AUD">AUD - Australian Dollar</SelectItem>
                      <SelectItem value="CAD">CAD - Canadian Dollar</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>GST Treatment</Label>
                  <Select value={editForm.gst_treatment || 'none'} onValueChange={v => setEditForm(f => ({...f, gst_treatment: v === 'none' ? '' : v}))}>
                    <SelectTrigger data-testid="edit-gst-treatment-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Not Applicable</SelectItem>
                      <SelectItem value="registered">Registered (Has GSTIN)</SelectItem>
                      <SelectItem value="consumer">Consumer (Unregistered)</SelectItem>
                      <SelectItem value="overseas">Overseas (Export)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              {editForm.gst_treatment === 'registered' && (
                <div>
                  <Label>GST ID *</Label>
                  <Input value={editForm.gst_id} onChange={e => setEditForm(f => ({...f, gst_id: e.target.value}))} placeholder="e.g. 29ABCDE1234F1Z5" data-testid="edit-gst-id-input" />
                </div>
              )}
              <div>
                <Label>Notes</Label>
                <Textarea value={editForm.notes} onChange={e => setEditForm(f => ({...f, notes: e.target.value}))} rows={3} />
              </div>
              <div>
                <Label>Status</Label>
                <Select value={editForm.status} onValueChange={v => setEditForm(f => ({...f, status: v}))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="archived">Archived</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setEditModalOpen(false)}>Cancel</Button>
              <Button onClick={handleEditSave} data-testid="save-edit-client-btn">Save Changes</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
